import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const HISTORY_OPTIONS = [
  'Abdominal Pain',
  'RUQ Pain',
  'Elevated LFTs',
  'Cholecystectomy',
  'Other'
];

const LIVER_ECHOTEXTURE = [
  'Normal',
  'Heterogeneous',
  'Coarse',
  'Fatty Infiltration',
  'Cirrhotic',
  'Not Well Visualized'
];

const GALLBLADDER_OPTIONS = [
  'Normal',
  'Stones',
  'Sludge',
  'Polyp',
  'Wall Thickening',
  'Pericholecystic Fluid',
  'Positive Sonographic Murphy\'s Sign',
  'Contracted',
  'Post Cholecystectomy',
  'Not Well Visualized'
];

const SPLEEN_OPTIONS = [
  'Normal',
  'Not Evaluated',
  'Enlarged',
  'Not Well Visualized'
];

const PANCREAS_OPTIONS = [
  'Normal',
  'Abnormal',
  'Not Well Visualized'
];

const KIDNEY_OPTIONS = [
  'Not Evaluated',
  'Normal',
  'Hydronephrosis',
  'Stone(s)',
  'Cyst(s)',
  'Other'
];

const COMMON_BILE_DUCT_STATUS = [
  'Normal (< 6mm)',
  'Mildly Dilated (6-8mm)',
  'Moderately Dilated (8-10mm)',
  'Severely Dilated (>10mm)',
  'Not Well Visualized'
];

const TABS = ['Liver', 'Veins', 'Gallbladder & CBD', 'Pancreas', 'Spleen', 'Kidneys', 'Other Findings'];

const HEPATIC_VEINS_OPTIONS = ['Patent', 'Occluded', 'Not Well Visualized'];
const PORTAL_VEIN_OPTIONS = ['Hepatopetal Flow', 'Hepatofugal Flow', 'Occluded', 'Not Well Visualized'];
const PARAUMBILICAL_VEIN_OPTIONS = ['Not Visualized', 'Patent'];

function AbdomenForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Liver, 1=Veins, 2=Gallbladder & CBD, 3=Pancreas, 4=Spleen, 5=Kidneys, 6=Other Findings
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();

  const INITIAL_ABDOMEN_DATA = {
    examType: 'Abdomen',
    examDate: new Date().toISOString().split('T')[0],
    firstName: '',
    lastName: '',
    mrn: '',
    technologist: '',
    reason: '',
    callReport: false,
    phoneNumber: '',
    history: '',
    comments: '',
    liver: {
      echotexture: 'Normal',
      size: '',
      other: '',
      masses: []
    },
    veins: {
      hepaticVeins: 'Patent',
      portalVein: 'Hepatopetal Flow',
      paraumbilicalVein: 'Not Visualized',
      other: ''
    },
    gallbladder: {
      status: ['Normal'],
      wallThickness: '',
      stones: [],
      other: ''
    },
    commonBileDuct: {
      status: 'Normal (< 6mm)',
      measurement: ''
    },
    pancreas: {
      status: 'Normal',
      other: ''
    },
    spleen: {
      status: 'Normal',
      size: '',
      other: ''
    },
    rightKidney: {
      status: ['Normal'],
      length: '',
      other: '',
      masses: []
    },
    leftKidney: {
      status: ['Not Evaluated'],
      length: '',
      other: '',
      masses: []
    },
    ascites: false,
    masses: []
  };

  const {
    formData,
    error,
    success,
    isLoading,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    updateField,
    updateNestedField,
    resetForm,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
    ...INITIAL_ABDOMEN_DATA,
    technologist: defaultTech // Auto-populate with current user
    },
    'Abdomen',
    {
      token, facilityInfo
    }
  );
  // Update technologist field when defaultTech loads
  useEffect(() => {
    if (defaultTech && !formData.technologist) {
        updateField('technologist', defaultTech);
     }
   }, [defaultTech, formData.technologist, updateField]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  }, [hookHandleSubmit]);

  const handleHistoryChange = (option) => {
    const newHistory = formData.history.includes(option)
      ? formData.history.filter(h => h !== option)
      : [...formData.history, option];

    updateField('history', newHistory);
  };

  const handleGallbladderStatusChange = (option) => {
    let newStatus;

    if (formData.gallbladder.status.includes(option)) {
      // Deselecting an option
      newStatus = formData.gallbladder.status.filter(s => s !== option);
      // If nothing is selected, default to 'Normal'
      if (newStatus.length === 0) {
        newStatus = ['Normal'];
      }
    } else {
      // Selecting an option
      if (option === 'Normal' || option === 'Not Well Visualized' || option === 'Post Cholecystectomy') {
        // These are exclusive - clear all others
        newStatus = [option];
      } else {
        // Remove 'Normal' if selecting something else
        newStatus = formData.gallbladder.status.filter(s => s !== 'Normal');
        newStatus.push(option);
      }
    }

    updateNestedField('gallbladder.status', newStatus);
  };

  const handleKidneyStatusChange = (kidney, option) => {
    const kidneyKey = kidney === 'right' ? 'rightKidney' : 'leftKidney';
    const currentStatus = formData[kidneyKey].status;
    const currentOther = formData[kidneyKey].other;
    let newStatus;
    let newOther = currentOther;

    if (currentStatus.includes(option)) {
      // Deselecting an option
      newStatus = currentStatus.filter(s => s !== option);
      // If nothing is selected, default to 'Normal'
      if (newStatus.length === 0) {
        newStatus = ['Normal'];
      }
    } else {
      // Selecting an option
      if (option === 'Not Evaluated') {
        // Not Evaluated is exclusive - clear all others
        newStatus = [option];
      } else if (option === 'Normal') {
        // Selecting Normal while Additional Findings still has text would
        // leave the form saying "Normal" while the free text describes
        // something else — ask before silently leaving that inconsistency.
        if (currentOther && currentOther.trim()) {
          const keepText = window.confirm(
            `Additional Findings still says:\n\n"${currentOther}"\n\nKeep this text even though the status is being set to Normal?\n\nClick OK to keep it, or Cancel to clear it.`
          );
          if (!keepText) {
            newOther = '';
          }
        }
        // Normal is exclusive - clear all others
        newStatus = [option];
      } else {
        // Remove 'Normal' and 'Not Evaluated' if selecting something else
        newStatus = currentStatus.filter(s => s !== 'Normal' && s !== 'Not Evaluated');
        newStatus.push(option);
      }
    }

    updateField(kidneyKey, { ...formData[kidneyKey], status: newStatus, other: newOther });
  };

  // Typing into Additional Findings implies an abnormal/other finding, so
  // auto-switch the status checkboxes to 'Other' the same way checking
  // 'Other' directly would — but only when text is actually entered
  // (clearing the field doesn't revert the checkboxes automatically).
  const handleKidneyOtherChange = (kidney, value) => {
    const kidneyKey = kidney === 'right' ? 'rightKidney' : 'leftKidney';
    const current = formData[kidneyKey];
    let newStatus = current.status;
    if (value.trim() && !current.status.includes('Other')) {
      newStatus = [...current.status.filter(s => s !== 'Normal' && s !== 'Not Evaluated'), 'Other'];
    }
    updateField(kidneyKey, { ...current, other: value, status: newStatus });
  };

  // Classify a CBD diameter (mm) into the matching COMMON_BILE_DUCT_STATUS option
  const classifyCBDMeasurement = (value) => {
    const mm = parseFloat(value);
    if (isNaN(mm)) return null;
    if (mm < 6) return 'Normal (< 6mm)';
    if (mm < 8) return 'Mildly Dilated (6-8mm)';
    if (mm < 10) return 'Moderately Dilated (8-10mm)';
    return 'Severely Dilated (>10mm)';
  };

  const handleCBDMeasurementChange = (value) => {
    const classifiedStatus = classifyCBDMeasurement(value);
    updateField('commonBileDuct', {
      ...formData.commonBileDuct,
      measurement: value,
      // Keep the status dropdown in sync with the entered diameter so the two
      // never disagree (e.g. a 7mm entry left on "Normal (< 6mm)").
      // Only overrides when a valid number is entered; "Not Well Visualized"
      // is unreachable here since the measurement field is hidden for it.
      status: classifiedStatus || formData.commonBileDuct.status
    });
  };

  const addMass = () => {
    const newMass = {
      location: '',
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Hypoechoic',
      description: ''
    };

    updateField('masses', [...formData.masses, newMass]);
  };

  const removeMass = (index) => {
    updateField('masses', formData.masses.filter((_, i) => i !== index));
  };

  const handleMassChange = (index, field, value) => {
    updateField('masses', formData.masses.map((mass, i) =>
      i === index ? { ...mass, [field]: value } : mass
    ));
  };

  const addGallstone = () => {
    const newStone = {
      size: '',
      description: 'Mobile'
    };

    updateNestedField('gallbladder.stones', [...formData.gallbladder.stones, newStone]);
  };

  const removeGallstone = (index) => {
    updateNestedField('gallbladder.stones', formData.gallbladder.stones.filter((_, i) => i !== index));
  };

  const handleGallstoneChange = (index, field, value) => {
    updateNestedField('gallbladder.stones', formData.gallbladder.stones.map((stone, i) =>
      i === index ? { ...stone, [field]: value } : stone
    ));
  };

  // Liver mass functions
  const addLiverMass = () => {
    const newMass = {
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Hypoechoic',
      description: ''
    };

    updateNestedField('liver.masses', [...formData.liver.masses, newMass]);
  };

  const removeLiverMass = (index) => {
    updateNestedField('liver.masses', formData.liver.masses.filter((_, i) => i !== index));
  };

  const handleLiverMassChange = (index, field, value) => {
    updateNestedField('liver.masses', formData.liver.masses.map((mass, i) =>
      i === index ? { ...mass, [field]: value } : mass
    ));
  };

  // Kidney mass functions
  const addKidneyMass = (kidney) => {
    const kidneyKey = kidney === 'right' ? 'rightKidney' : 'leftKidney';
    const newMass = {
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Hypoechoic',
      description: ''
    };

    updateNestedField(`${kidneyKey}.masses`, [...formData[kidneyKey].masses, newMass]);
  };

  const removeKidneyMass = (kidney, index) => {
    const kidneyKey = kidney === 'right' ? 'rightKidney' : 'leftKidney';
    updateNestedField(`${kidneyKey}.masses`, formData[kidneyKey].masses.filter((_, i) => i !== index));
  };

  const handleKidneyMassChange = (kidney, index, field, value) => {
    const kidneyKey = kidney === 'right' ? 'rightKidney' : 'leftKidney';
    updateNestedField(`${kidneyKey}.masses`, formData[kidneyKey].masses.map((mass, i) =>
      i === index ? { ...mass, [field]: value } : mass
    ));
  };

  const renderMassEditor = (mass, index, onChange, onRemove, titlePrefix) => (
    <div key={index} className="mass-section">
      <h4>{titlePrefix} {index + 1}</h4>

      <div className="size-inputs">
        <label>Size (cm):</label>
        <input type="number" step="0.1" placeholder="L" value={mass.size1} onChange={(e) => onChange(index, 'size1', e.target.value)} />
        <span>×</span>
        <input type="number" step="0.1" placeholder="W" value={mass.size2} onChange={(e) => onChange(index, 'size2', e.target.value)} />
        <span>×</span>
        <input type="number" step="0.1" placeholder="H" value={mass.size3} onChange={(e) => onChange(index, 'size3', e.target.value)} />
      </div>

      <div className="form-group">
        <label>Echotexture:</label>
        <select value={mass.echotexture} onChange={(e) => onChange(index, 'echotexture', e.target.value)}>
          <option value="Hypoechoic">Hypoechoic</option>
          <option value="Hyperechoic">Hyperechoic</option>
          <option value="Anechoic">Anechoic</option>
          <option value="Complex">Complex</option>
          <option value="Heterogeneous">Heterogeneous</option>
        </select>
      </div>

      <div className="form-group">
        <label>Description:</label>
        <textarea rows="2" value={mass.description} onChange={(e) => onChange(index, 'description', e.target.value)} placeholder="Additional details about the mass" />
      </div>

      <button type="button" onClick={() => onRemove(index)} className="remove-btn">
        Remove Mass
      </button>
    </div>
  );

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button
              type="button"
              className="btn-close"
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            {TABS.map((tabLabel, tabIndex) => (
              <button
                key={tabLabel}
                type="button"
                onClick={() => setActiveTab(tabIndex)}
                className={`worksheet-tab-btn${activeTab === tabIndex ? ' active' : ''}`}
              >
                {tabLabel}
              </button>
            ))}
          </div>

          <div className="worksheet-tab-content">
            {/* Liver Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Liver</h3>

                <div className="form-group">
                  <label>Echotexture:</label>
                  <select
                    value={formData.liver.echotexture}
                    onChange={(e) => updateNestedField('liver.echotexture', e.target.value)}
                  >
                    {LIVER_ECHOTEXTURE.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {formData.liver.echotexture !== 'Not Well Visualized' && (
                  <div className="form-group">
                    <label>Liver Size (cm):</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Craniocaudal dimension"
                      value={formData.liver.size}
                      onChange={(e) => updateNestedField('liver.size', e.target.value)}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Additional Findings:</label>
                  <input
                    type="text"
                    value={formData.liver.other}
                    onChange={(e) => updateNestedField('liver.other', e.target.value)}
                    placeholder="Other observations"
                  />
                </div>

                <details className="collapsible-subsection" open={formData.liver.masses.length > 0}>
                  <summary>Masses / Lesions{formData.liver.masses.length > 0 ? ` (${formData.liver.masses.length})` : ''}</summary>
                  <button type="button" onClick={addLiverMass} className="add-mass-btn">
                    + Add Mass/Lesion
                  </button>
                  {formData.liver.masses.map((mass, index) =>
                    renderMassEditor(mass, index, handleLiverMassChange, removeLiverMass, 'Liver Mass/Lesion')
                  )}
                </details>
              </div>
            )}

            {/* Veins Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Veins</h3>

                <div className="form-group">
                  <label>Hepatic Veins:</label>
                  <select
                    value={formData.veins.hepaticVeins}
                    onChange={(e) => updateNestedField('veins.hepaticVeins', e.target.value)}
                  >
                    {HEPATIC_VEINS_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Portal Vein:</label>
                  <select
                    value={formData.veins.portalVein}
                    onChange={(e) => updateNestedField('veins.portalVein', e.target.value)}
                  >
                    {PORTAL_VEIN_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Paraumbilical Vein:</label>
                  <select
                    value={formData.veins.paraumbilicalVein}
                    onChange={(e) => updateNestedField('veins.paraumbilicalVein', e.target.value)}
                  >
                    {PARAUMBILICAL_VEIN_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Additional Findings:</label>
                  <input
                    type="text"
                    value={formData.veins.other}
                    onChange={(e) => updateNestedField('veins.other', e.target.value)}
                    placeholder="Other observations"
                  />
                </div>
              </div>
            )}

            {/* Gallbladder & CBD Tab */}
            {activeTab === 2 && (
              <div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Gallbladder</h3>

                <div className="form-group">
                  <label>Status (select all that apply):</label>
                  <div className="checkbox-group">
                    {GALLBLADDER_OPTIONS.map(option => (
                      <label key={option}>
                        <input
                          type="checkbox"
                          checked={formData.gallbladder.status.includes(option)}
                          onChange={() => handleGallbladderStatusChange(option)}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Gallbladder Wall Thickness (mm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Normal < 3.0 mm"
                    value={formData.gallbladder.wallThickness}
                    onChange={(e) => updateNestedField('gallbladder.wallThickness', e.target.value)}
                  />
                </div>

                {formData.gallbladder.status.includes('Stones') && (
                  <details className="collapsible-subsection" open={formData.gallbladder.stones.length > 0}>
                    <summary>Stones{formData.gallbladder.stones.length > 0 ? ` (${formData.gallbladder.stones.length})` : ''}</summary>
                    <button type="button" onClick={addGallstone} className="add-mass-btn">
                      + Add Stone
                    </button>
                    {formData.gallbladder.stones.map((stone, index) => (
                      <div key={index} className="mass-section">
                        <h4>Stone {index + 1}</h4>
                        <div className="form-group">
                          <label>Size (mm):</label>
                          <input
                            type="number"
                            step="0.1"
                            value={stone.size}
                            onChange={(e) => handleGallstoneChange(index, 'size', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <label>Description:</label>
                          <input
                            type="text"
                            value={stone.description}
                            onChange={(e) => handleGallstoneChange(index, 'description', e.target.value)}
                            placeholder="e.g., Mobile, Shadowing"
                          />
                        </div>
                        <button type="button" onClick={() => removeGallstone(index)} className="remove-btn">
                          Remove Stone
                        </button>
                      </div>
                    ))}
                  </details>
                )}

                {!formData.gallbladder.status.includes('Not Well Visualized') &&
                 !formData.gallbladder.status.includes('Post Cholecystectomy') && (
                  <div className="form-group">
                    <label>Additional Findings:</label>
                    <input
                      type="text"
                      value={formData.gallbladder.other}
                      onChange={(e) => updateNestedField('gallbladder.other', e.target.value)}
                      placeholder="Other observations"
                    />
                  </div>
                )}

                <h3 style={{ color: 'var(--color-primary)', margin: '25px 0 20px' }}>Common Bile Duct</h3>

                <div className="form-group">
                  <label>Status:</label>
                  <select
                    value={formData.commonBileDuct.status}
                    onChange={(e) => updateNestedField('commonBileDuct.status', e.target.value)}
                  >
                    {COMMON_BILE_DUCT_STATUS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {formData.commonBileDuct.status !== 'Not Well Visualized' && (
                  <div className="form-group">
                    <label>Measurement (mm):</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Diameter"
                      value={formData.commonBileDuct.measurement}
                      onChange={(e) => handleCBDMeasurementChange(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Pancreas Tab */}
            {activeTab === 3 && (
              <div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Pancreas</h3>

                <div className="form-group">
                  <label>Status:</label>
                  <select
                    value={formData.pancreas.status}
                    onChange={(e) => updateNestedField('pancreas.status', e.target.value)}
                  >
                    {PANCREAS_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Additional Findings:</label>
                  <input
                    type="text"
                    value={formData.pancreas.other}
                    onChange={(e) => updateNestedField('pancreas.other', e.target.value)}
                    placeholder="Other observations"
                  />
                </div>
              </div>
            )}

            {/* Spleen Tab */}
            {activeTab === 4 && (
              <div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Spleen</h3>

                <div className="form-group">
                  <label>Status:</label>
                  <select
                    value={formData.spleen.status}
                    onChange={(e) => updateNestedField('spleen.status', e.target.value)}
                  >
                    {SPLEEN_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {formData.spleen.status !== 'Not Well Visualized' && formData.spleen.status !== 'Not Evaluated' && (
                  <div className="form-group">
                    <label>Size (cm):</label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="Length (Normal < 12.5cm)"
                      value={formData.spleen.size}
                      onChange={(e) => updateNestedField('spleen.size', e.target.value)}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Additional Findings:</label>
                  <input
                    type="text"
                    value={formData.spleen.other}
                    onChange={(e) => updateNestedField('spleen.other', e.target.value)}
                    placeholder="Other observations"
                  />
                </div>
              </div>
            )}

            {/* Kidneys Tab */}
            {activeTab === 5 && (
              <div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Kidneys (Limited View)</h3>

                <h4>Right Kidney</h4>
                <div className="form-group">
                  <label>Status (select all that apply):</label>
                  <div className="checkbox-group checkbox-group-compact">
                    {KIDNEY_OPTIONS.map(option => (
                      <label key={option} style={{
                        opacity: formData.rightKidney.status.includes('Not Evaluated') && option !== 'Not Evaluated' ? 0.5 : 1
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.rightKidney.status.includes(option)}
                          onChange={() => handleKidneyStatusChange('right', option)}
                          disabled={formData.rightKidney.status.includes('Not Evaluated') && option !== 'Not Evaluated'}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Length (cm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Renal length"
                    value={formData.rightKidney.length}
                    onChange={(e) => updateNestedField('rightKidney.length', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Additional Findings:</label>
                  <input
                    type="text"
                    value={formData.rightKidney.other}
                    onChange={(e) => handleKidneyOtherChange('right', e.target.value)}
                    placeholder="Other observations"
                  />
                </div>

                <details className="collapsible-subsection" open={formData.rightKidney.masses.length > 0}>
                  <summary>Right Kidney Masses / Lesions{formData.rightKidney.masses.length > 0 ? ` (${formData.rightKidney.masses.length})` : ''}</summary>
                  <button type="button" onClick={() => addKidneyMass('right')} className="add-mass-btn">
                    + Add Mass/Lesion
                  </button>
                  {formData.rightKidney.masses.map((mass, index) =>
                    renderMassEditor(mass, index, (i, f, v) => handleKidneyMassChange('right', i, f, v), (i) => removeKidneyMass('right', i), 'Right Kidney Mass/Lesion')
                  )}
                </details>

                <h4 style={{ marginTop: '25px' }}>Left Kidney</h4>
                <div className="form-group">
                  <label>Status (select all that apply):</label>
                  <div className="checkbox-group checkbox-group-compact">
                    {KIDNEY_OPTIONS.map(option => (
                      <label key={option} style={{
                        opacity: formData.leftKidney.status.includes('Not Evaluated') && option !== 'Not Evaluated' ? 0.5 : 1
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.leftKidney.status.includes(option)}
                          onChange={() => handleKidneyStatusChange('left', option)}
                          disabled={formData.leftKidney.status.includes('Not Evaluated') && option !== 'Not Evaluated'}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Length (cm):</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="Renal length"
                    value={formData.leftKidney.length}
                    onChange={(e) => updateNestedField('leftKidney.length', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Additional Findings:</label>
                  <input
                    type="text"
                    value={formData.leftKidney.other}
                    onChange={(e) => handleKidneyOtherChange('left', e.target.value)}
                    placeholder="Other observations"
                  />
                </div>

                <details className="collapsible-subsection" open={formData.leftKidney.masses.length > 0}>
                  <summary>Left Kidney Masses / Lesions{formData.leftKidney.masses.length > 0 ? ` (${formData.leftKidney.masses.length})` : ''}</summary>
                  <button type="button" onClick={() => addKidneyMass('left')} className="add-mass-btn">
                    + Add Mass/Lesion
                  </button>
                  {formData.leftKidney.masses.map((mass, index) =>
                    renderMassEditor(mass, index, (i, f, v) => handleKidneyMassChange('left', i, f, v), (i) => removeKidneyMass('left', i), 'Left Kidney Mass/Lesion')
                  )}
                </details>
              </div>
            )}

            {/* Other Findings Tab */}
            {activeTab === 6 && (
              <div>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Other Findings</h3>

                <div style={{ marginBottom: '15px' }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.ascites}
                      onChange={(e) => updateField('ascites', e.target.checked)}
                    />
                    Ascites Present
                  </label>
                </div>

                <details className="collapsible-subsection" open={formData.masses.length > 0}>
                  <summary>Masses / Lesions{formData.masses.length > 0 ? ` (${formData.masses.length})` : ''}</summary>
                  <button type="button" onClick={addMass} className="add-mass-btn">
                    + Add Mass/Lesion
                  </button>
                  {formData.masses.map((mass, index) => (
                    <div key={index} className="mass-section">
                      <h4>Mass/Lesion {index + 1}</h4>

                      <div className="form-group">
                        <label>Location:</label>
                        <input
                          type="text"
                          placeholder="e.g., Right lobe of liver, Pancreatic head"
                          value={mass.location}
                          onChange={(e) => handleMassChange(index, 'location', e.target.value)}
                        />
                      </div>

                      <div className="size-inputs">
                        <label>Size (cm):</label>
                        <input type="number" step="0.1" placeholder="L" value={mass.size1} onChange={(e) => handleMassChange(index, 'size1', e.target.value)} />
                        <span>×</span>
                        <input type="number" step="0.1" placeholder="W" value={mass.size2} onChange={(e) => handleMassChange(index, 'size2', e.target.value)} />
                        <span>×</span>
                        <input type="number" step="0.1" placeholder="H" value={mass.size3} onChange={(e) => handleMassChange(index, 'size3', e.target.value)} />
                      </div>

                      <div className="form-group">
                        <label>Echotexture:</label>
                        <select
                          value={mass.echotexture}
                          onChange={(e) => handleMassChange(index, 'echotexture', e.target.value)}
                        >
                          <option value="Hypoechoic">Hypoechoic</option>
                          <option value="Hyperechoic">Hyperechoic</option>
                          <option value="Anechoic">Anechoic</option>
                          <option value="Complex">Complex</option>
                          <option value="Heterogeneous">Heterogeneous</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Description:</label>
                        <textarea
                          rows="2"
                          value={mass.description}
                          onChange={(e) => handleMassChange(index, 'description', e.target.value)}
                          placeholder="Additional details about the mass"
                        />
                      </div>

                      <button type="button" onClick={() => removeMass(index)} className="remove-btn">
                        Remove Mass
                      </button>
                    </div>
                  ))}
                </details>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button type="submit" disabled={isLoading} className="submit-btn">
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button type="button" onClick={resetForm} className="clear-btn">
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default AbdomenForm;
